import React, { useEffect, useState } from "react";
import { BellRing, Smartphone, Vibrate, Volume2, Send, Info } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Label } from "../../components/common";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { pushSupported, isIOS, isStandalone, currentSubscription, subscribePush, unsubscribePush } from "../../lib/push";

function Row({ icon: Icon, title, text, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-4 last:border-0" data-testid={testId}>
      <div className="flex gap-3">
        <Icon className="mt-0.5 h-5 w-5 text-indigo-600" />
        <div><p className="font-semibold text-slate-800">{title}</p><p className="text-sm text-slate-500">{text}</p></div>
      </div>
      {children}
    </div>
  );
}

export default function MyNotifications() {
  const [cfg, setCfg] = useState(null);
  const [onDevice, setOnDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem("sv_ping_muted") !== "1");
  const supported = pushSupported();
  const iosNeedsInstall = isIOS() && !isStandalone();

  useEffect(() => {
    api.get("/push/config").then((r) => setCfg(r.data)).catch(() => toast.error("Could not load settings"));
    currentSubscription().then((s) => setOnDevice(!!s)).catch(() => {});
  }, []);

  const toggleDevice = async (on) => {
    setBusy(true);
    try {
      if (on) { await subscribePush(cfg.public_key); toast.success("Phone notifications turned on for this device"); }
      else { await unsubscribePush(); toast.success("Phone notifications turned off for this device"); }
      setOnDevice(on);
    } catch (e) { toast.error(e.message || apiError(e)); }
    finally { setBusy(false); }
  };
  const toggleVibrate = async (v) => {
    setCfg((c) => ({ ...c, vibrate: v }));
    try { await api.put("/push/prefs", { vibrate: v }); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSound = (v) => {
    setSound(v);
    localStorage.setItem("sv_ping_muted", v ? "0" : "1");
    window.dispatchEvent(new Event("sv-ping-pref"));
  };
  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      data.sent ? toast.success("Test notification sent") : toast.error("No device is set up yet. Turn on phone notifications first.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="my-notifications-page">
      <div>
        <Label>Just for you</Label>
        <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">My notifications</h1>
        <p className="mt-1 text-sm text-slate-500">Get a phone alert, like a new message, every time a customer leaves a rating. These settings only affect you.</p>
      </div>

      {iosNeedsInstall && (
        <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="ios-install-hint">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>On iPhone, first tap <b>Share → Add to Home Screen</b>, open Servantage from the new icon, then come back here to turn notifications on.</span>
        </div>
      )}
      {!supported && !iosNeedsInstall && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="push-unsupported">This browser doesn't support phone notifications. Try Chrome on Android, or Safari on iPhone after adding Servantage to your Home Screen.</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white px-5">
        <Row icon={Smartphone} title="Phone notifications on this device" testId="row-push-device"
          text={cfg ? `Shows a notification with your phone's normal notification sound. ${cfg.devices} device(s) set up.` : "Loading…"}>
          <Switch checked={onDevice} disabled={!cfg || !supported || busy} onCheckedChange={toggleDevice} data-testid="push-device-switch" />
        </Row>
        <Row icon={Vibrate} title="Vibrate" text="Buzz the phone when a notification arrives (Android)." testId="row-push-vibrate">
          <Switch checked={cfg?.vibrate ?? true} disabled={!cfg} onCheckedChange={toggleVibrate} data-testid="push-vibrate-switch" />
        </Row>
        <Row icon={Volume2} title="Ping sound while the app is open" text="Plays a soft ping in Servantage when a rating comes in." testId="row-app-sound">
          <Switch checked={sound} onCheckedChange={toggleSound} data-testid="app-sound-switch" />
        </Row>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={sendTest} disabled={!onDevice} data-testid="push-test-btn"><Send className="mr-1 h-4 w-4" /> Send test notification</Button>
        <span className="flex items-center gap-1 text-xs text-slate-400"><BellRing className="h-3.5 w-3.5" /> Notification sound and volume follow your phone's settings.</span>
      </div>
    </div>
  );
}
